/* eslint-disable @typescript-eslint/no-unused-vars */
import * as Colors from '@brightlayer-ui/colors';
import { Box, Button, CircularProgress, Theme, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../../lib/AlertContext';
import { FEATURE_FLAGS } from '../../lib/featureFlags/featureFlagConstants';
import { useFeatureFlag } from '../../lib/featureFlags/FeatureFlagProvider';
import { OrganizationService } from '../../services/organization/organization.service';
import { ExternalApplication, Organization } from '../../services/organization/types';

const styles = {
    registerButton: {
        '&:hover': {
            bgcolor: Colors.blue[300],
        },
    },
    revokeButton: {
        backgroundColor: Colors.red[500],
        color: Colors.white[50],
        '&:hover': {
            bgcolor: Colors.red[300],
        },
    },
    ableEdgeContainer: (theme: Theme): object => ({
        width: 'auto',
        [theme.breakpoints.down('sm')]: {
            width: 'auto',
        },
    }),
    sectionHeader: {
        paddingBottom: '8px',
        color: Colors.blue[400],
    },
    textContainer: {
        marginBottom: '16px',
        width: '50%',
    },
    registerInfoText: { paddingBottom: '8px' },
    buttonContainer: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
    },
};

type ManageAppRegistrationProps = {
    organization: Organization;
    isUserOwner: boolean;
    isCredentialCreated: boolean;
    externalApp: ExternalApplication | undefined;
    setExternalApp: (externalApp: ExternalApplication | undefined) => void;
};

export const ManageAppRegistration: React.FC<ManageAppRegistrationProps> = (props) => {
    const { organization, isUserOwner, isCredentialCreated, externalApp, setExternalApp } = props;

    const [updateInProgress, setUpdateInProgress] = useState(false);
    const { showConfirmationToUser, showInfoAlertToUser } = useAlert();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isInstallerInvitationEnabled = useFeatureFlag(FEATURE_FLAGS.INSTALLER_INVITATION_ENABLED);

    const [isRegistered, setIsRegistered] = useState(
        externalApp ? externalApp.organizations.includes(organization.id) : false
    );
    const handleOnClickRegister = async (): Promise<void> => {
        try {
            setUpdateInProgress(true);
            await OrganizationService.shareOrgCredentialsWithExternalApplication({
                organizationId: organization.id,
            });
            setIsRegistered(true);
            /*this is to fetch the updated external application after registration
            to update the state of the component as the external application is not updated in the store immediately after registration
            we need to fetch it again to get the updated list of organizations
            that the external application is registered with */
            const updatedExternalApp = await OrganizationService.getExternalApplication().catch(() => undefined);
            setExternalApp(updatedExternalApp);
        } catch (e) {
            showInfoAlertToUser({
                title: t('ORGANIZATION_DETAILS_PAGE.FAILED_TO_REGISTER_ORG_TITLE'),
                description: t('ORGANIZATION_DETAILS_PAGE.FAILED_TO_REGISTER_ORG_BODY', {
                    organizationName: organization.name,
                }),
            });
        } finally {
            setUpdateInProgress(false);
        }
    };

    const renderConfirmRegisterModal = (): void => {
        showConfirmationToUser({
            title: t('ORGANIZATION_DETAILS_PAGE.CONFIRM_ORG_REGISTRATION_TITLE'),
            description: t('ORGANIZATION_DETAILS_PAGE.CONFIRM_ORG_REGISTRATION_BODY', {
                organizationName: organization.name,
            }),
            onConfirm: (): void => void handleOnClickRegister(),
            confirmText: t('COMMON.YES'),
            cancelText: t('COMMON.NO'),
        });
    };

    const handleOnClickRevoke = async (): Promise<void> => {
        try {
            setUpdateInProgress(true);
            await OrganizationService.revokeOrgCredentialsFromExternalApplication({
                organizationId: organization.id,
            });
            setExternalApp(undefined);
            setIsRegistered(false);
        } catch (e) {
            showInfoAlertToUser({
                title: t('ORGANIZATION_DETAILS_PAGE.FAILED_TO_REVOKE_ORG_TITLE'),
                description: t('ORGANIZATION_DETAILS_PAGE.FAILED_TO_REVOKE_ORG_BODY', {
                    organizationName: organization.name,
                }),
            });
        } finally {
            setUpdateInProgress(false);
        }
    };

    const renderConfirmRevokeModal = (): void => {
        showConfirmationToUser({
            title: t('ORGANIZATION_DETAILS_PAGE.CONFIRM_ORG_REVOKE_TITLE'),
            description: t('ORGANIZATION_DETAILS_PAGE.CONFIRM_ORG_REVOKE_BODY', {
                organizationName: organization.name,
            }),
            onConfirm: (): void => void handleOnClickRevoke(),
            confirmText: t('COMMON.YES'),
            cancelText: t('COMMON.NO'),
        });
    };

    return (
        <Box data-testid="register-org-container">
            <Typography variant="h6" sx={styles.sectionHeader}>
                {t('ORGANIZATION_DETAILS_PAGE.REGISTER_ORG_HEADER')}
            </Typography>
            <Box sx={styles.textContainer}>
                <Typography variant="body2" sx={styles.registerInfoText}>
                    {isRegistered
                        ? t('ORGANIZATION_DETAILS_PAGE.ORG_ALREADY_REGISTERED', {
                              organizationName: organization.name,
                          })
                        : t('ORGANIZATION_DETAILS_PAGE.REGISTER_ORG')}
                </Typography>

                <Typography variant="body2">{t('ORGANIZATION_DETAILS_PAGE.REGISTER_ORG_EXPLANATION')}</Typography>
            </Box>
            <Box sx={styles.buttonContainer}>
                {isRegistered && isInstallerInvitationEnabled && (
                    <Button
                        data-testid="manage-installers-button"
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={updateInProgress}
                        onClick={(): void =>
                            navigate(`/organizations/${organization.id}/manageInstallers`, {
                                state: { organization },
                            })
                        }
                    >
                        {t('ORGANIZATION_DETAILS_PAGE.MANAGE_INSTALLERS_BUTTON')}
                    </Button>
                )}
                {isUserOwner && (
                    <Button
                        data-testid={isRegistered ? 'unregister-org-button' : 'register-org-button'}
                        variant="contained"
                        color={isRegistered ? 'error' : 'primary'}
                        size="large"
                        sx={isRegistered ? styles.revokeButton : styles.registerButton}
                        disabled={isCredentialCreated || updateInProgress}
                        onClick={(): void =>
                            !isRegistered ? renderConfirmRegisterModal() : renderConfirmRevokeModal()
                        }
                    >
                        {updateInProgress ? (
                            <CircularProgress sx={{ color: 'white' }} size={20} variant={'indeterminate'} />
                        ) : isRegistered ? (
                            t('COMMON.REVOKE')
                        ) : (
                            t('COMMON.REGISTER')
                        )}
                    </Button>
                )}
            </Box>
        </Box>
    );
};
